import {
  setDayWay,
  updateAgendaItem,
  type CityWay,
  type DayLeg,
  type Plan,
  type Point,
  type Ride,
  type Road,
  type WayPart,
  type WaySaid,
} from '@trippath/shared';

import { agendaInputOf, type Step } from './cityDays';
import type { LineKind } from '../../lib/map';

/** The ways a town's timetables know: asked of them for their lines and minutes. */
export const TRANSIT = new Set<CityWay>([
  'metro',
  'tram',
  'train',
  'cable',
  'bus',
  'minibus',
  'ferry',
]);

/** How a ride is drawn on the map: by its kind's colour, a town's own ways with their like. */
export const LINE_OF: Record<CityWay, LineKind> = {
  walk: 'walk',
  bike: 'bike',
  scooter: 'bike',
  cyclo: 'bike',
  metro: 'metro',
  tram: 'tram',
  train: 'train',
  cable: 'cable',
  // Shared on the road: a bus, and the town's own that go a set way.
  bus: 'bus',
  minibus: 'bus',
  jeepney: 'bus',
  songthaew: 'bus',
  // Hired to take one where one says.
  tuktuk: 'taxi',
  motortaxi: 'taxi',
  taxi: 'taxi',
  ridehail: 'taxi',
  car: 'car',
  boat: 'ferry',
  ferry: 'ferry',
};

/** How far apart two points are as the crow flies, in metres. */
export function apart(a: Point, b: Point): number {
  const rad = Math.PI / 180;
  const x = (b.lon - a.lon) * rad * Math.cos(((a.lat + b.lat) / 2) * rad);
  return Math.hypot(x, (b.lat - a.lat) * rad) * 6_371_000;
}

/**
 * Which of the day's own ways a step is, those that belong to no thing: back to the room, to the
 * room checked into, on to the way out of town; `null` for the way to a thing, which is the
 * thing's.
 */
export function legOf(step: Step): DayLeg | null {
  if (step.to.kind === 'back') return 'back';
  if (step.to.kind === 'leave') return 'leave';
  return step.to.kind === 'stay' ? 'in' : null;
}

/** One part of a step: by what, from and to where, its end's name, its minutes when said. */
export interface Segment {
  by: CityWay;
  from: Point;
  to: Point;
  name: string;
  minutes: number | null;
}

/**
 * A step in its parts as said: from the airport by train to the centre, by bus to a stop, on
 * foot to the hotel; said in one, one part; said not at all, walked.
 */
export function segmentsOf(
  step: Step,
  said: WaySaid | null,
  end: string,
): [Segment, ...Segment[]] {
  const [first, ...rest] = said?.parts ?? [];
  if (!first) {
    return [
      {
        by: said?.by ?? 'walk',
        from: step.start,
        to: step.end,
        name: end,
        minutes: said?.byMinutes ?? null,
      },
    ];
  }
  const part = (one: WayPart, from: Point): Segment => ({
    by: one.by,
    from,
    to: one.to ?? step.end,
    name: one.to?.name ?? end,
    minutes: one.minutes,
  });
  const head = part(first, step.start);
  const tail = rest.reduce<Segment[]>(
    (done, one) => [...done, part(one, (done.at(-1) ?? head).to)],
    [],
  );
  return [head, ...tail];
}

/** A part as found: how long, how far, whether only roughly, how it is drawn, its ride. */
export interface Resolved {
  minutes: number | null;
  metres: number;
  rough: boolean;
  lines: { kind: LineKind; path: [number, number][] }[];
  ride: Ride | null;
}

/**
 * A part with its walk along the streets or its ride by the timetables: its minutes as said, else
 * as found; a ride drawn leg by leg, the walks to and from its stops dotted apart from it.
 */
export function resolve(segment: Segment, walk: Road | null, ride: Ride | null): Resolved {
  const straight: [number, number][] = [
    [segment.from.lon, segment.from.lat],
    [segment.to.lon, segment.to.lat],
  ];
  const metres = apart(segment.from, segment.to);
  if (walk) {
    return {
      minutes: segment.minutes ?? walk.minutes,
      metres: walk.metres,
      rough: false,
      lines: [{ kind: 'walk', path: walk.path }],
      ride: null,
    };
  }
  const kind = LINE_OF[segment.by];
  if (ride) {
    const legs = ride.legs.filter((leg) => leg.path.length > 1);
    return {
      minutes: segment.minutes ?? ride.minutes,
      metres,
      rough: true,
      lines:
        legs.length > 0
          ? legs.map((leg) => ({ kind: leg.mode === 'walk' ? 'walk' : kind, path: leg.path }))
          : [{ kind, path: ride.path.length > 1 ? ride.path : straight }],
      ride,
    };
  }
  return {
    minutes: segment.minutes,
    metres,
    rough: true,
    lines: [{ kind, path: straight }],
    ride,
  };
}

/**
 * A step's way kept as said: with the thing it goes to, or as one of the day's own ways; a way to
 * where the day begins is no one's, and is not kept.
 */
export function keepWay(
  plan: Plan,
  stop: string,
  day: string,
  step: Step,
  way: WaySaid,
): Promise<Plan> {
  if (step.to.kind === 'thing') {
    const input = agendaInputOf(stop, step.to.item, plan.trip.currency, way);
    return updateAgendaItem(plan.trip.id, step.to.item.id, input);
  }
  const own = legOf(step);
  return own
    ? setDayWay(plan.trip.id, stop, [day, own], way)
    : Promise.reject(new Error('A way to where the day begins is no one’s.'));
}
